/**
 * Phase 27b — Approver break-override dialog.
 *
 * Shows the raw duration vs deducted duration, forces a required note,
 * and calls POST /api/time/entries/{id}/break-override with {note}.
 *
 * Props:
 *   entry, open, onClose, onOverridden
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function formatH(mins) {
  return `${(Number(mins || 0) / 60).toFixed(2)}h`;
}
function formatMins(mins) {
  const n = Number(mins || 0);
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function BreakOverrideDialog({
  entry, open, onClose, onOverridden,
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setNote("");
      setErr("");
    }
  }, [open]);

  if (!entry) return null;

  const raw = Number(entry.raw_minutes || 0);
  const paid = Number(entry.duration_minutes || 0);
  const deducted = Math.max(0, raw - paid);
  const ruleDeducted = Number(entry.rule_deducted_minutes || 0);

  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/time/entries/${entry.id}/break-override`,
        { note: note.trim() }
      );
      const restored = Math.max(
        0,
        Number(data.duration_minutes || 0) - paid
      );
      toast.success(
        restored > 0
          ? `Break override applied — ${formatMins(restored)} restored`
          : "Break override applied"
      );
      onOverridden && onOverridden(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(
        typeof detail === "string"
          ? detail
          : e?.message || "Could not apply the override."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="break-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Override missing meal break
          </DialogTitle>
          <DialogDescription>
            Restores the rule-based deduction on this entry. Required note is
            saved to the audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Raw
                </div>
                <div className="mt-0.5 font-display text-lg tabular-nums">
                  {formatH(raw)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Deducted
                </div>
                <div className="mt-0.5 font-display text-lg tabular-nums text-amber-700">
                  −{formatH(deducted)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Paid
                </div>
                <div className="mt-0.5 font-display text-lg tabular-nums text-primary">
                  {formatH(paid)}
                </div>
              </div>
            </div>
            {ruleDeducted > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                Override restores <span className="font-mono">{formatMins(ruleDeducted)}</span>{" "}
                from the Phase-27 rule. Other tenant-policy deductions
                (auto-deduct) remain in effect.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="override-note">
              Reason for override <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="override-note"
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Worker documented an on-call meal — 30 min restored per workspace policy."
              data-testid="break-override-note"
            />
            <div className="text-xs text-muted-foreground">
              Required — this text is saved to the entry's audit trail.
            </div>
          </div>

          {err && (
            <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !note.trim()}
            data-testid="break-override-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
